import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FacilityDTO, FacilityFloorDTO } from "../Facility";
import { TitleService } from "src/app/title.service";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { StatesService } from "src/app/shared/states/states.service";
import { MatDialog } from "@angular/material/dialog";
import { Address } from "ngx-google-places-autocomplete/objects/address";
import { FacilityService } from "../facility.service";
import { SessionService } from "src/app/session.service";
import { NotificationService } from "src/app/notifications/notification.service";
import { AreYouSureDialogComponent } from "src/app/shared/dialogs/are-you-sure-dialog/are-you-sure-dialog.component";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { BeaconDTO } from "src/app/beacons/Beacon";
import { BeaconService } from "src/app/beacons/beacon.service";
import { GooglePlacesUtil } from "src/app/shared/google-places-util/google-places-util";
import { UiService } from "src/app/ui/ui.service";
import { switchMap, takeUntil, tap } from "rxjs/operators";
import { Subject } from "rxjs";
import { M, T } from "@angular/cdk/keycodes";

@Component({
  selector: "app-facility",
  templateUrl: "./facility.component.html",
  styleUrls: ["./facility.component.scss"],
})
export class FacilityComponent implements OnInit {
  newFacility!: boolean;
  facility?: FacilityDTO;
  facilityForm!: FormGroup;
  statesOptions: any;
  locationName: any;
  placeName: string = "";
  newFloorName: string = "";
  deleteFloors: Array<FacilityFloorDTO> = [];
  unassignedBeacons!: BeaconDTO[];
  beaconSearchText: string = "";
  newEmailControl = new FormControl("", [
    Validators.email,
    Validators.required,
  ]);
  draggingFloors: boolean = false;
  private onComponentDestroy = new Subject();
  contactEmails!: string[];

  constructor(
    private activatedRoute: ActivatedRoute,
    private TitleService: TitleService,
    private StatesService: StatesService,
    private dialog: MatDialog,
    private router: Router,
    private FacilityService: FacilityService,
    private SessionService: SessionService,
    private NotificationService: NotificationService,
    private BeaconService: BeaconService,
    private uiService: UiService,
  ) {
    this.statesOptions = this.StatesService.states;
  }

  ngOnInit(): void {
    this.facility = this.activatedRoute.snapshot.data.facility;
    this.contactEmails = this.facility?.contact_emails ?? [];
    this.unassignedBeacons =
      this.activatedRoute.snapshot.data.unassignedBeacons?.records;
    this.newFacility = !this.facility;

    if (!this.newFacility) {
      this.TitleService.setTitle(
        this.TitleService.getTitle() + ` | ${this.facility?.name}`,
      );
    }

    this.initForms();
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  initForms() {
    this.facilityForm = new FormGroup({
      placeName: new FormControl(this.placeName),
      facilityName: new FormControl(this.facility?.name, [Validators.required]),
      facilityAddress: new FormControl(
        this.facility?.address1,
        Validators.required,
      ),
      suiteUnitEtc: new FormControl(this.facility?.address2),
      city: new FormControl(this.facility?.city, Validators.required),
      state: new FormControl(this.facility?.state, Validators.required),
      zip: new FormControl(this.facility?.zip, Validators.required),
      lat: new FormControl(this.facility?.lat, Validators.required),
      lng: new FormControl(this.facility?.lng, Validators.required),
      foreign_id: new FormControl(this.facility?.foreign_id),
    });
  }

  get facilityDTO(): FacilityDTO {
    let facilityDTO = {
      name: this.facilityForm.get("facilityName")?.value,
      address1: this.facilityForm.get("facilityAddress")?.value,
      address2: this.facilityForm.get("suiteUnitEtc")?.value,
      city: this.facilityForm.get("city")?.value,
      state: this.facilityForm.get("state")?.value,
      zip: this.facilityForm.get("zip")?.value,
      lat: this.facilityForm.get("lat")?.value,
      lng: this.facilityForm.get("lng")?.value,
      foreign_id: this.facilityForm.get("foreign_id")?.value,
      contact_emails: this.contactEmails,
    };

    if (this.facility) {
      facilityDTO = { ...this.facility, ...facilityDTO };
    }

    return facilityDTO as any as FacilityDTO;
  }

  get saveDisabled() {
    return this.facilityForm.invalid;
  }

  get currentAccount() {
    return this.SessionService.currentAccountValue!;
  }

  onPlaceChange(event: Address) {
    //console.log(event)
    this.placeName = "";
    this.facilityForm.patchValue({
      placeName: "",
      facilityName:
        event.types.length > 1
          ? event.name
          : this.facilityForm.get("facilityName")?.value,
      facilityAddress:
        GooglePlacesUtil.findAddressComponentByType(event, "street_number") +
        " " +
        GooglePlacesUtil.findAddressComponentByType(event, "route"),
      suiteUnitEtc: this.facilityForm.get("suiteUnitEtc")?.value,
      city: GooglePlacesUtil.findAddressComponentByType(event, "locality"),
      state: GooglePlacesUtil.findAddressComponentByType(
        event,
        "administrative_area_level_1",
      ),
      zip: GooglePlacesUtil.findAddressComponentByType(event, "postal_code"),
      lat: event.geometry.location.lat(),
      lng: event.geometry.location.lng(),
      foreign_id: this.facilityForm.get("foreign_id")?.value,
    });
  }

  saveNewFacility() {
    this.uiService
      .waitForEvent(
        this.FacilityService.createFacility(
          this.currentAccount.accountID,
          this.facilityDTO as FacilityDTO,
        ).pipe(
          tap(
            (saved) => {
              let facil: FacilityDTO = <FacilityDTO>(<unknown>saved);
              this.NotificationService.openSuccessSnackBar(
                "Facility created successfully",
              );
              this.router.navigate([facil.id], {
                relativeTo: this.activatedRoute.parent,
              });
            },
            () => {
              this.NotificationService.openErrorSnackBar(
                "Error creating facility",
              );
            },
          ),
          switchMap(() =>
            this.FacilityService.getFacilities(this.currentAccount.accountID, {
              page: 0,
              perPage: 999999999,
            }),
          ),
        ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (facilities) => {
          this.SessionService.updateFacilities(facilities.records);
        },
        () => {
          this.NotificationService.openErrorSnackBar(
            "Error retrieving facilities",
          );
        },
      );
  }

  saveFacilityUpdates() {
    // Deleted Floors
    this.deleteFloors.forEach((fl) => {
      this.uiService
        .waitForEvent(
          this.FacilityService.deleteFloor(this.currentAccount.accountID, fl),
        )
        .pipe(takeUntil(this.onComponentDestroy))
        .subscribe(
          () => {},
          (err) => {
            console.log("Error deleting floor");
          },
        );
    });

    // Update Beacons
    let updateFloorBeacons = (floor: FacilityFloorDTO) => {
      floor.beacons?.forEach((b) => {
        b.facility_floor_id = floor.id;
        this.uiService
          .waitForEvent(
            this.BeaconService.updateBeacon(this.currentAccount.accountID, b),
          )
          .pipe(takeUntil(this.onComponentDestroy))
          .subscribe(
            (saved) => {},
            (err) => {
              console.log("Error updating beacon", err);
            },
          );
      });
    };

    // New and Updated Floors
    this.facility?.floors?.forEach((fl) => {
      if (fl.id !== undefined) {
        this.uiService
          .waitForEvent(
            this.FacilityService.updateFloor(this.currentAccount.accountID, fl),
          )
          .pipe(takeUntil(this.onComponentDestroy))
          .subscribe(
            () => {
              updateFloorBeacons(fl);
            },
            (err) => {
              console.log("Error updating floor", err);
            },
          );
      } else {
        this.uiService
          .waitForEvent(
            this.FacilityService.createFloor(this.currentAccount.accountID, fl),
          )
          .pipe(takeUntil(this.onComponentDestroy))
          .subscribe(
            (saved: any) => {
              // apply saved id to local, do not return saved
              fl.id = saved.id;
              updateFloorBeacons(fl as FacilityFloorDTO);
            },
            (err) => {
              console.log("Error creating floor", err);
            },
          );
      }
    });

    // Unassigned Beacons
    this.unassignedBeacons.forEach((ub) => {
      this.uiService
        .waitForEvent(
          this.BeaconService.updateBeacon(this.currentAccount.accountID, ub),
        )
        .pipe(takeUntil(this.onComponentDestroy))
        .subscribe(
          () => {},
          (err) => {
            console.log("Error updating unassigned beacon");
          },
        );
    });

    // Facility
    this.uiService
      .waitForEvent(
        this.FacilityService.updateFacility(
          this.currentAccount.accountID,
          this.facilityDTO as FacilityDTO,
        ).pipe(
          tap(
            () => {
              this.NotificationService.openSuccessSnackBar(
                "Facility updated successfully",
              );
              this.deleteFloors = [];
            },
            () => {
              this.NotificationService.openErrorSnackBar(
                "Error updating facility",
              );
            },
          ),
          switchMap(() =>
            this.FacilityService.getFacilities(this.currentAccount.accountID, {
              page: 0,
              perPage: 999999999,
            }),
          ),
        ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (facilities) => {
          this.SessionService.updateFacilities(facilities.records);
        },
        () => {
          this.NotificationService.openErrorSnackBar(
            "Error retrieving facilities",
          );
        },
      );
  }

  cancel() {
    this.router.navigate(["list"], {
      relativeTo: this.activatedRoute.parent!.parent,
    });
  }

  deleteFacility() {
    this.dialog
      .open(AreYouSureDialogComponent, {
        data: {
          areYouSureInfo: {
            description: `Are you sure you want to delete this facility ( ${
              this.facility!.name
            } )?`,
            warning: "Warning: this is not reversible!",
          },
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((result) => {
        if (result) {
          this.uiService
            .waitForEvent(
              this.FacilityService.deleteFacility(
                this.currentAccount.accountID,
                this.facility!.id,
              ).pipe(
                tap(
                  () => {
                    this.NotificationService.openSuccessSnackBar(
                      "Facility deleted",
                    );

                    this.router.navigate(["list"], {
                      relativeTo: this.activatedRoute.parent!.parent,
                    });
                  },
                  () => {
                    this.NotificationService.openErrorSnackBar(
                      "Error deleting facility",
                    );
                  },
                ),
                switchMap(() =>
                  this.FacilityService.getFacilities(
                    this.currentAccount.accountID,
                    {
                      page: 0,
                      perPage: 999999999,
                    },
                  ),
                ),
              ),
            )
            .pipe(takeUntil(this.onComponentDestroy))
            .subscribe(
              (facilities) => {
                this.SessionService.updateFacilities(facilities.records);
              },
              () => {
                this.NotificationService.openErrorSnackBar(
                  "Error retrieving facilities",
                );
              },
            );
        }
      });
  }

  addNewFloor() {
    // do not add if name is not unique
    let existing = this.facility?.floors?.find((ff) => {
      return ff.name === this.newFloorName;
    });
    if (existing !== undefined) {
      return;
    }

    let lastFloor = this.facility?.floors
      ? this.facility.floors[this.facility?.floors.length - 1]
      : null;
    let newFloor = {
      facility_id: this.facility?.id,
      name: this.newFloorName,
      sort_order: lastFloor ? lastFloor.sort_order + 100 : 0,
      beacons: [] as BeaconDTO[],
    } as FacilityFloorDTO;
    console.log(newFloor);
    this.facility?.floors?.push(newFloor);
    this.newFloorName = "";
  }

  floorDragDrop(event: CdkDragDrop<any>) {
    // moves item in floors array
    moveItemInArray(
      this.facility?.floors!,
      event.previousIndex,
      event.currentIndex,
    );
    // updates sort orders based on new array order
    this.facility?.floors?.forEach((fl, index) => {
      fl.sort_order = (index + 1) * 100;
    });
  }

  deleteFloor(index: number) {
    let floor: FacilityFloorDTO | undefined = this.facility?.floors?.[index];
    if (floor !== undefined) {
      // remove beacons
      floor.beacons?.forEach((b) => {
        this.onBeaconRemove(b);
      });
      // add the floor to floors to delete
      this.deleteFloors.push({ ...floor });
      this.facility?.floors?.splice(index, 1);
    }
  }

  onDropBeacon(event: CdkDragDrop<any>) {
    // ignore if container has not changed
    if (
      event.container.data.obj.name === event.previousContainer.data.obj.name
    ) {
      return;
    }

    // if target is unassigned, remove from floor and add to unassigned (same as removal by click)
    if (event.container.data.list === "unassigned") {
      this.onBeaconRemove(event.item.data);
    }

    // if source was unassigned
    else if (event.previousContainer.data.list === "unassigned") {
      // remove from unassigned
      this.unassignedBeacons.forEach((ub, index) => {
        if (ub.id === event.item.data.id) {
          this.unassignedBeacons.splice(index, 1);
        }
      });
      // find the floor (may not be saved yet)
      let facilityFloor = this.facility?.floors?.filter((floor) => {
        return floor.id !== undefined
          ? floor.id == event.container.data.obj.id
          : floor.name === event.container.data.obj.name;
      })[0];
      // update the beacon with floor assignment
      event.item.data.facility_floor_id =
        facilityFloor?.id !== undefined ? facilityFloor.id : null;
      event.item.data.facilityFloor = facilityFloor;
      // add the beacon to the floor
      facilityFloor?.beacons?.push(event.item.data);
    }

    // if source was another floor
    else {
      // find the source floor (may not be saved yet)
      let sourceFloor = this.facility?.floors?.filter((floor) => {
        return floor.id !== undefined
          ? floor.id == event.previousContainer.data.obj.id
          : floor.name === event.previousContainer.data.obj.name;
      })[0];
      // find the destination floor (may not be saved yet)
      let destFloor = this.facility?.floors?.filter((floor) => {
        return floor.id !== undefined
          ? floor.id == event.container.data.obj.id
          : floor.name === event.container.data.obj.name;
      })[0];
      // update the beacon with the new floor
      event.item.data.facility_floor_id =
        destFloor?.id !== undefined ? destFloor.id : null;
      event.item.data.facilityFloor = destFloor;
      // remove the beacon from the source floor
      sourceFloor?.beacons?.forEach((sb, index) => {
        if (sb.id === event.item.data.id) {
          sourceFloor?.beacons?.splice(index, 1);
        }
      });
      // add the beacon to the destination floor
      destFloor?.beacons?.push(event.item.data);
    }
  }

  onBeaconRemove(beacon: BeaconDTO) {
    // Find floor (might not be saved yet)
    let facilityFloor = this.facility?.floors?.filter((floor) => {
      return floor.id !== undefined
        ? floor.id == beacon.facilityFloor?.id
        : floor.name === beacon.facilityFloor?.name;
    })[0];
    // remove beacon from floor
    facilityFloor?.beacons?.forEach((fb, index) => {
      if (fb.id === beacon.id) {
        facilityFloor?.beacons?.splice(index, 1);
      }
    });
    // remove floor from beacon
    beacon.facilityFloor = undefined;
    beacon.facility_floor_id = undefined;
    // Add to unassigned
    this.unassignedBeacons.push(beacon);
  }

  cdkDragStarted() {
    this.draggingFloors = true;
  }

  cdkDragReleased() {
    this.draggingFloors = false;
  }

  addNewEmail() {
    this.contactEmails.push(this.newEmailControl.value);
    this.newEmailControl.reset();
  }

  deleteEmail(emailIndex: number) {
    this.contactEmails.splice(emailIndex, 1);
  }
}
